import type { Placement } from '@floating-ui/react';
import { memo } from 'react';
import { clsx } from 'clsx';
import type { RenditionSet, RenditionUrl } from '@library';
import { ImageWithRenditions, Tooltip } from '@library';
import ProductPlaceholder from './ProductPlaceholder';
import SimilarImageIndicator from './SimilarImageIndicator';
import styles from './ProductImage.module.scss';

type RenditionSize = 'mini' | 'small' | 'medium' | 'large';
const smallResolution = '176px';
const mediumResolution = '352px';
const largeResolution = '704px';
const hugeResolution = '1080p';

const renditionSets: RenditionSet[] = [
    { rendition: 'mini', set: [smallResolution, smallResolution] },
    {
        rendition: 'small',
        set: [smallResolution, mediumResolution],
    },
    {
        rendition: 'medium',
        set: [mediumResolution, largeResolution],
    },
    {
        rendition: 'large',
        set: [largeResolution, hugeResolution],
    },
];

type Size = '352px' | '250px' | '176px' | '100px' | '60px' | '26px';

interface BaseProductImageProps {
    /**
     * The url of image. * would be replaced to needed rendition.
     */
    url?: RenditionUrl;
    /**
     * Show the similar image indicator
     */
    isSimilarImage?: boolean;
    /**
     * Default placement of overview tooltip
     */
    overviewPlacement?: Placement;
    /**
     * The alt attribute of image
     */
    alt?: string;
    /**
     * Show preview in modal with correct z-index
     */
    modal?: boolean;
    /**
     * Disable the lazy loading of image
     */
    disableLazy?: boolean;
}

export type StretchProductImageProps = BaseProductImageProps & {
    renditionSize: RenditionSize;
    size?: never;
};

export type FixedProductImageProps = BaseProductImageProps & {
    renditionSize?: never;
    size: Size;
};

export type ProductImageProps =
    | StretchProductImageProps
    | FixedProductImageProps;

/**
 * The product image component. Render product image in needed rendition size.
 * Shows placeholder if no image is provided or wrong.
 * Supports overview tooltip.
 * Supports similar image indicator.
 */
const ProductImage = ({
    url,
    isSimilarImage,
    alt = 'product image',
    overviewPlacement,
    modal,
    disableLazy,
    ...props
}: ProductImageProps) => {
    const { renditionSize } = props;

    return (
        <Tooltip
            placement={overviewPlacement}
            content={
                <div className={styles.imageOverview} data-testid="imagePopup">
                    <ProductImage
                        size="352px"
                        url={url}
                        disableLazy={disableLazy}
                    />
                </div>
            }
            modal={modal}
            disabled={!overviewPlacement || !url}
            showArrow
            theme={styles.theme}
        >
            <div
                className={clsx(styles.container, {
                    [styles.dynamicSize]: !props.size,
                })}
            >
                <ImageWithRenditions
                    alt={alt}
                    placeholder={
                        <ProductPlaceholder
                            withoutText={
                                renditionSize === 'mini' ||
                                props.size === '60px' ||
                                props.size === '100px'
                            }
                        />
                    }
                    renditionUrl={url}
                    renditionSet={renditionSets}
                    {...props}
                    disableLazy={disableLazy}
                />
                {isSimilarImage && renditionSize !== 'mini' && (
                    <SimilarImageIndicator centred />
                )}
            </div>
        </Tooltip>
    );
};

export default memo(ProductImage);
